import '@99designs/apps/spa/public-path';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import React, { useEffect } from 'react';
import { hydrate } from 'react-dom';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter } from 'react-router-dom';
import { ApolloProvider } from '@apollo/client';
import Bugsnag from '@bugsnag/js';
import { loadableReady } from '@loadable/component';
// eslint-disable-next-line no-restricted-imports
import { ThemeProvider } from '@material-ui/core/styles';
import createBrowserClient from '@99designs/apps/spa/common/ApolloClient/createBrowserClient';
import { ExperimentsData, ExperimentsProvider } from '@99designs/experiments';
import { SiteConfig } from '@99designs/frontend-components/apps/global-nav/lib/Site';
// TODO: Move `AppConfigContext` to a more appropriate lib.
import { AppConfigData, AppConfigProvider } from '@99designs/layout';
import { AccountsJs } from './AccountsJs';
import App from './App';
import materialTheme from './materialTheme';

// Setup here
const backend = createBrowserClient({
    endpoint: '/app/graphql',
    cacheDiv: 'apollo-init-data-backend',
});

const siteDataNode = document.getElementById('site-init-data');
const siteState = JSON.parse(siteDataNode!.textContent!); // eslint-disable-line @typescript-eslint/no-non-null-assertion

const experimentsDataNode = document.getElementById('experiments-init-data');
const experimentsData: ExperimentsData = experimentsDataNode?.textContent
    ? JSON.parse(experimentsDataNode.textContent)
    : { experiments: '' };

const appConfigNode = document.getElementById('app-config-init-data');
const appConfigData: AppConfigData = appConfigNode?.textContent
    ? JSON.parse(appConfigNode.textContent)
    : {};

const HotReloadableRoot = ({
    language,
    siteConfig,
}: {
    language: string;
    siteConfig: SiteConfig;
}) => {
    useEffect(() => {
        const jssStyles = document.querySelector('#jss-server-side');
        if (jssStyles?.parentElement) {
            jssStyles.parentElement.removeChild(jssStyles);
        }
    }, []);
    return (
        <ThemeProvider theme={materialTheme}>
            <App
                language={language}
                siteConfig={siteConfig}
                backendSubscriptionClient={backend.subscriptionClient}
            />
            <AccountsJs />
        </ThemeProvider>
    );
};

const renderApp = () => {
    const htmlNode = document.getElementsByTagName('html');
    const language = htmlNode[0].getAttribute('lang')!; // eslint-disable-line @typescript-eslint/no-non-null-assertion

    Bugsnag.start({
        apiKey: appConfigData.bugsnagApiKey || '_',
        appType: __BUGSNAG_APP_TYPE__,
        appVersion: appConfigData.appVersion || 'dev',
        enabledReleaseStages: ['production'],
        releaseStage: appConfigData.bugsnagReleaseStage,
        enabledBreadcrumbTypes: [
            'navigation',
            'request',
            'process',
            'log',
            'user',
            'state',
            'error',
            'manual',
        ],
        plugins: [new BugsnagPluginReact(React)],
    });

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const BugsnagErrorBoundary = Bugsnag.getPlugin('react')!.createErrorBoundary();

    hydrate(
        <BugsnagErrorBoundary>
            <ApolloProvider client={backend.combinedClient}>
                <AppConfigProvider value={appConfigData}>
                    <ExperimentsProvider value={experimentsData}>
                        <BrowserRouter>
                            <HelmetProvider>
                                <HotReloadableRoot language={language} siteConfig={siteState} />
                            </HelmetProvider>
                        </BrowserRouter>
                    </ExperimentsProvider>
                </AppConfigProvider>
            </ApolloProvider>
        </BugsnagErrorBoundary>,
        document.getElementById('root'),
        () => {
            window.initialised = true;
        }
    );
};

if (!window.Intl) {
    document.write(
        '<script src="https://assets.99static.com/shared/js/Intl.min-856e66ee99.js"></script>'
    );
}

window.init = () => {
    loadableReady(() => {
        renderApp();
    });
};
